import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from 'react-query'
import updateGameday from '../../../../Apis/gamedays/updateGameday'
import QueryKeys from '../../../../utils/constants/queryKeys'
import editGamedayModalStyles from './editGamedayModal.styles'
import CalendarIcon from '../../../../assets/icons/calendarIcon'

const useStyles = makeStyles(editGamedayModalStyles)

const EditGamedayModal = (props) => {
  const { isOpen, onClose, orgId, seasonId, groupId, gameday } = props
  const classes = useStyles()
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState({
    name: '',
    startDate: null,
    endDate: null,
  })

  useEffect(() => {
    if (isOpen && gameday) {
      setFormData({
        name: gameday.name || '',
        startDate: gameday.startDate ? new Date(gameday.startDate) : null,
        endDate: gameday.endDate ? new Date(gameday.endDate) : null,
      })
    } else if (!isOpen) {
      setFormData({ name: '', startDate: null, endDate: null })
    }
  }, [isOpen, gameday])

  const updateGamedayMutation = useMutation(
    (data) => updateGameday({ orgId, gamedayId: gameday?.id, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        onClose()
      },
    }
  )

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value })
  }

  const handleDateChange = (field) => (date) => {
    setFormData({ ...formData, [field]: date })
  }

  const handleSubmit = () => {
    updateGamedayMutation.mutate({
      groupId,
      name: formData.name,
      startDate: formData.startDate ? formData.startDate.toISOString() : null,
      endDate: formData.endDate ? formData.endDate.toISOString() : null,
    })
  }

  const isLoading = updateGamedayMutation.isLoading

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit Gameday</DialogTitle>
      <DialogContent>
        <TextField
          label='Gameday Name'
          fullWidth
          value={formData.name}
          onChange={handleInputChange('name')}
          className={classes.dialogField}
          margin='normal'
          inputProps={{ maxLength: 100 }}
          required
        />
        <KeyboardDatePicker
          label='Start Date'
          format='dd/MM/yyyy'
          fullWidth
          value={formData.startDate}
          onChange={handleDateChange('startDate')}
          className={classes.dialogField}
          margin='normal'
          inputVariant='outlined'
          keyboardIcon={<CalendarIcon />}
          InputLabelProps={{ shrink: true }}
        />
        <KeyboardDatePicker
          label='End Date'
          format='dd/MM/yyyy'
          fullWidth
          value={formData.endDate}
          onChange={handleDateChange('endDate')}
          className={classes.dialogField}
          margin='normal'
          inputVariant='outlined'
          keyboardIcon={<CalendarIcon />}
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color='primary'
          variant='contained'
          disabled={isLoading || !formData.name}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

EditGamedayModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  seasonId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  gameday: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
}

export default EditGamedayModal
